import { Head, Link, router } from "@inertiajs/react"
import { Sparkles, GraduationCap } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import { samplesPath, newSamplePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Muestras",
    href: samplesPath(),
  },
  {
    title: "Seleccionar Formato",
    href: "/samples/select-format",
  },
]

const productTypeLabels: Record<string, string> = {
  coffee: "Café",
}

interface FormatOption {
  value: string
  title: string
  description: string
  icon: any
  color: string
  iconColor: string
  hoverColor: string
  features: string[]
}

const formatsByProduct: Record<string, FormatOption[]> = {
  coffee: [
    {
      value: "simplified",
      title: "Formato Simplificado",
      description: "Ideal para evaluaciones rápidas y generales",
      icon: Sparkles,
      color: "bg-blue-100 dark:bg-blue-900",
      iconColor: "text-blue-600 dark:text-blue-300",
      hoverColor: "hover:border-blue-600",
      features: [
        "Evaluación rápida y sencilla",
        "Menos campos requeridos",
        "Perfecto para principiantes",
        "Enfoque en características principales",
      ],
    },
    {
      value: "professional",
      title: "Formato Profesional",
      description: "Evaluación completa siguiendo protocolos SCA",
      icon: GraduationCap,
      color: "bg-primary/10",
      iconColor: "text-primary",
      hoverColor: "hover:border-primary",
      features: [
        "Protocolo completo SCA",
        "Evaluación detallada de todos los atributos",
        "Cálculo automático de puntajes",
        "Ideal para catadores certificados",
      ],
    },
  ],
}

interface Props {
  product_type: string
}

export default function SelectFormat({ product_type }: Props) {
  const formats = formatsByProduct[product_type] || []

  const handleFormatSelect = (format: string) => {
    router.get(
      `${newSamplePath()}?product_type=${product_type}&cupping_format=${format}`,
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Seleccionar Formato de Evaluación" />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div>
          <div className="mb-2 flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">
              Seleccionar Formato de Evaluación
            </h1>
            <span className="bg-primary/10 text-primary inline-flex items-center rounded-full px-3 py-1 text-sm font-medium">
              {productTypeLabels[product_type]}
            </span>
          </div>
          <p className="text-muted-foreground">
            Elige el nivel de detalle para tu muestra
          </p>
        </div>

        {/* Format Cards */}
        <div className="grid max-w-4xl gap-6 md:grid-cols-2">
          {formats.map((format) => {
            const Icon = format.icon
            return (
              <Card
                key={format.value}
                className={`cursor-pointer border-2 transition-all hover:shadow-lg ${format.hoverColor}`}
                onClick={() => handleFormatSelect(format.value)}
              >
                <CardHeader>
                  <div
                    className={`mb-4 rounded-full ${format.color} w-fit p-4`}
                  >
                    <Icon className={`h-12 w-12 ${format.iconColor}`} />
                  </div>
                  <CardTitle className="text-2xl">{format.title}</CardTitle>
                  <CardDescription className="mb-4 text-base">
                    {format.description}
                  </CardDescription>
                  <div className="border-t pt-4">
                    <p className="mb-3 text-sm font-medium">Características:</p>
                    <ul className="space-y-2">
                      {format.features.map((feature, index) => (
                        <li
                          key={index}
                          className="text-muted-foreground flex items-start gap-2 text-sm"
                        >
                          <span className="text-primary mt-0.5">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardHeader>
              </Card>
            )
          })}
        </div>

        {/* Back Button */}
        <div className="max-w-4xl">
          <Link href={samplesPath()}>
            <Button variant="outline">← Volver a Muestras</Button>
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
